import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { SolverIcon } from '@/shared/components/Icon/SolverIcon'
import { MultiselectDropdown } from '@/shared/components/MultiselectDropdown/MultiselectDropdown'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { TextField } from '@/shared/components/TextField/TextField'
import { TimePicker } from '@/shared/components/TimePicker/TimePicker'
import { pxToRem } from '@/shared/styles/themes'
import { Divider, Grid2 as Grid, Typography } from '@mui/material'

type GeneralTabProps = {
  isReadOnly: boolean
  depotOptions: DropdownOption[]
  attributeOptions: DropdownOption[]
}

export const GeneralTab: React.FC<GeneralTabProps> = ({
  isReadOnly,
  depotOptions,
  attributeOptions,
}) => {
  const { control } = useFormContext()
  
  return (
    <Grid container spacing={1}>
      <Grid size={{ xs: 12 }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mt: 1,
            mb: 1,
          }}
        >
          General Run Information
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="description"
          control={control}
          rules={{ required: 'Description is required' }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Description"
              placeholder="Enter run description"
              disabled={isReadOnly}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="startDepotId"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <>
                <SelectDropdown
                  {...field}
                  label="Start Depot"
                  placeholder="Select start depot"
                  options={depotOptions}
                  value={field.value}
                  onChange={(_, value) => {
                    field.onChange(value)
                  }}
                  getOptionLabel={(option) => {
                    if (!option) return ''

                    return (option as DropdownOption).label
                  }}
                  isOptionEqualToValue={(option, selectedOption) => {
                    return (option as DropdownOption).value === (selectedOption as DropdownOption).value
                  }}
                  disabled={isReadOnly}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              </>
            )
          }}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="finishDepotId"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <SelectDropdown
                {...field}
                label="Finish Depot"
                placeholder="Select finish depot"
                value={field.value}
                options={depotOptions}
                onChange={(_, value) => {
                  field.onChange(value)
                }}
                getOptionLabel={(option) => {
                  if (!option) return ''

                  return (option as DropdownOption).label
                }}
                isOptionEqualToValue={(option, selectedOption) => {
                  return (option as DropdownOption).value === (selectedOption as DropdownOption).value
                }}
                disabled={isReadOnly}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider
          sx={{
            mt: 2,
            mb: 1,
          }}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mb: 1,
          }}
        >
          Run Details
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Controller
          name="attributeIds"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <MultiselectDropdown
                {...field}
                ref={field.ref}
                label="Attributes"
                placeholder="Select attributes"
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                options={attributeOptions}
                getOptionLabel={(option) => {
                  return (option as DropdownOption).label
                }}
                isOptionEqualToValue={(option, selectedOption) => {
                  return (option as DropdownOption).value === (selectedOption as DropdownOption).value
                }}
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="weightCapacity"
          control={control}
          rules={{
            required: 'Weight capacity is required',
            min: {
              value: 0,
              message: 'Weight capacity must be greater than 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label="Weight Capacity"
                placeholder="Enter weight capacity"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="volumeCapacity"
          control={control}
          rules={{
            required: 'Volume capacity is required',
            min: {
              value: 0,
              message: 'Volume capacity must be greater than 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label="Volume Capacity"
                placeholder="Enter volume capacity"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            </>
          )}
        />
      </Grid>


      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="loadTime"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TimePicker
                {...field}
                label="Load Time"
                disabled={isReadOnly}
                onAccept={field.onChange}
                format="HH:mm"
                views={['hours', 'minutes']}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'HH:mm',
                    error: !!fieldState?.error,
                    helperText: fieldState?.error?.message,
                  },
                }}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="unloadTime"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TimePicker
                {...field}
                label="Unload Time"
                disabled={isReadOnly}
                onAccept={field.onChange}
                format="HH:mm"
                views={['hours', 'minutes']}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: 'HH:mm',
                    error: !!fieldState?.error,
                    helperText: fieldState?.error?.message,
                  },
                }}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="speedScale"
          control={control}
          rules={{
            required: 'Speed scale is required',
            min: {
              value: 0.01,
              message: 'Speed scale must be greater than 0.01',
            },
            max: {
              value: 1,
              message: 'Speed scale must be less than or equal to 1',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label="Speed Scale"
                placeholder="Enter speed scale"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0.01,
                  max: 1,
                }}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="targetUtilisation"
          control={control}
          rules={{
            required: 'Target utilization is required',
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Target Utilization (%)</>}
                placeholder="Enter target utilization"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            </>
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="maxLoads"
          control={control}
          rules={{
            required: 'Maximum loads is required',
            min: {
              value: 0,
              message: 'Maximum loads must be greater than or equal to 0',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                type="number"
                label={<><SolverIcon />Maximum Number of Loads</>}
                placeholder="Enter maximum loads"
                disabled={isReadOnly}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                inputProps={{
                  step: 1,
                  min: 0,
                }}
              />
            </>
          )}
        />
      </Grid>
    </Grid>
  )
}